import { Component, ViewChild, ElementRef, AfterViewChecked, OnInit, ViewEncapsulation, HostListener } from '@angular/core';
import { ChatbotService } from './chatbot.service';

interface Message {
  role: 'user' | 'bot';
  text: string;
  loading?: boolean;
  time?: string;
  rows?: any[];      // raw data rows for CSV download
  suggestions?: string[];   // follow-up chips shown under this bot message
  isAnswer?: boolean;       // true only for real /chat answers — gates copy/like/dislike
  logId?: string;           // backend log_id, used to attach feedback to this exact answer
  feedback?: 'like' | 'dislike' | null;
  copied?: boolean;         // brief "Copied" icon-swap state
}

interface TopicCategory {
  key: string;
  label: string;
  icon: string;
  items: string[];   // leaf topics — sent to backend as the `module` hint
}

interface LanguageOption {
  code: string;    // sent to backend as `language`
  label: string;   // shown on the toggle button
}

// ── Curated topic tree shown in the picker ──────────────────────────────
// This is a UI-only grouping (NOT the DB `modules` table — that stays
// dynamic/role-driven server-side). Leaf items are sent as free-text
// `module` hints to bias semantic table retrieval; they do NOT need to
// match literal module names, and the backend never blocks on them unless
// they happen to match a real role-gated module (see chat_router.py).
const TOPIC_TREE: TopicCategory[] = [
  {
    key: 'project_planning',
    label: 'Project Planning',
    icon: '🗂️',
    items: [
      'Work Order Upload',
      'Material BOQ Preparation',
      'BOQ Amendment List',
      'Kick Off Meeting',
      'Project Execution Schedule',
      'Material Delivery Schedule',
    ],
  },
  {
    key: 'material_planning',
    label: 'Material Planning',
    icon: '🧱',
    items: [
      'Material BOQ Preparation',
      'Material Delivery Schedule',
      'BOQ Amendment List',
      'Material Return',
      'Stock Transfer',
    ],
  },
  {
    key: 'project_tracking',
    label: 'Project Tracking',
    icon: '📍',
    items: [
      'Site Work Completion %',
      'Site Planning Progress',
      'Project Execution Schedule',
      'Kick Off Meeting Status',
      'Sub Contractor Work Order',
    ],
  },
  {
    key: 'dpr',
    label: 'DPR (Daily Planning Report)',
    icon: '📋',
    items: [
      'Daily Progress Report',
      "Today's Site Activity",
      'Material Usage Report',
    ],
  },
  {
    key: 'pending_approvals',
    label: 'Pending Approvals',
    icon: '⏳',
    items: [
      'Pending PRN',
      'Pending Purchase Orders',
      'Pending GRN',
      'Pending Expense',
      'Pending Site Planning',
      'Pending BOQ',
      'Pending Sub Contractor WO',
      'Pending Delivery Order',
    ],
  },
  {
    key: 'stock_transactions',
    label: 'Stock Transactions',
    icon: '📦',
    items: [
      'Stock / Inventory Levels',
      'Godown to Godown Transfer',
      'GRN',
      'Material Return',
      'Stock Adjustment',
    ],
  },
  {
    key: 'payments',
    label: 'Payments',
    icon: '💳',
    items: [
      'Client Payment',
      'Expense Entry',
      'Invoice',
      'Ledger',
      'Supplier Advance Payment',
      'Contractor Payment',
    ],
  },
  {
    key: 'purchases',
    label: 'Purchases',
    icon: '🛒',
    items: [
      'PRN',
      'Purchase Order',
      'GRN',
      'LOI',
      'Sub Contractor',
      'Quotation',
    ],
  },
];

// ── Supported reply languages ────────────────────────────────────────────
// Codes must match _LANGUAGE_NAMES in services/answer_formatter.py.
const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
];

const LANGUAGE_STORAGE_KEY = 'chatLanguage';

// Greeting shown on load, and a short confirmation line shown right after
// switching -- both keyed by language code so the very first thing the
// user sees already matches their chosen language, not just future replies.
const GREETING_TEXT: Record<string, (name: string) => string> = {
  en: (name) => `Hi ${name}! 👋 I'm your ERP Assistant\nPick a topic below, or just type your question — I'll figure it out either way.`,
  hi: (name) => `नमस्ते ${name}! 👋 मैं आपका ERP सहायक हूँ\nनीचे से कोई विषय चुनें, या सीधे अपना सवाल टाइप करें — मैं समझ जाऊँगा।`,
  mr: (name) => `नमस्कार ${name}! 👋 मी तुमचा ERP सहाय्यक आहे\nखालून एखादा विषय निवडा, किंवा थेट तुमचा प्रश्न टाइप करा — मी समजून घेईन.`,
};

const LANGUAGE_SWITCH_CONFIRM: Record<string, string> = {
  en: "Switched to English. I'll reply in English from now on.",
  hi: 'हिंदी में बदल दिया गया। अब से मैं हिंदी में जवाब दूँगा।',
  mr: 'मराठीमध्ये बदलले. आतापासून मी मराठीत उत्तर देईन.',
};

// ── Static widget text that must switch with `language` too ─────────────
const UI_TEXT: Record<string, Record<string, string>> = {
  en: {
    replyIn: 'Reply in:',
    justLetMeType: 'Just let me type ✍️',
    backToTopics: '← Back to topics',
    whatWouldYouLikeToKnowAbout: 'What would you like to know about',
    tapTopicBelow: 'Tap a topic above, or just type your question below.',
    topicLabel: 'Topic:',
    change: 'Change',
    browseTopics: 'Browse topics',
    inputPlaceholder: 'Ask about orders, stock, employees...',
  },
  hi: {
    replyIn: 'भाषा चुनें:',
    justLetMeType: 'मुझे बस टाइप करने दें ✍️',
    backToTopics: '← विषयों पर वापस जाएं',
    whatWouldYouLikeToKnowAbout: 'आपको इसके बारे में क्या जानना है',
    tapTopicBelow: 'ऊपर से कोई विषय चुनें, या नीचे अपना प्रश्न टाइप करें।',
    topicLabel: 'विषय:',
    change: 'बदलें',
    browseTopics: 'विषय ब्राउज़ करें',
    inputPlaceholder: 'ऑर्डर, स्टॉक, कर्मचारियों के बारे में पूछें...',
  },
  mr: {
    replyIn: 'भाषा निवडा:',
    justLetMeType: 'मला फक्त टाइप करू द्या ✍️',
    backToTopics: '← विषयांकडे परत जा',
    whatWouldYouLikeToKnowAbout: 'तुम्हाला याबद्दल काय जाणून घ्यायचे आहे',
    tapTopicBelow: 'वर एखादा विषय निवडा, किंवा खाली तुमचा प्रश्न टाइप करा.',
    topicLabel: 'विषय:',
    change: 'बदला',
    inputPlaceholder: 'ऑर्डर, स्टॉक, कर्मचाऱ्यांबद्दल विचारा...',
  },
};

const SELECT_MODULE_TEXT: Record<string, (topic: string) => string> = {
  en: (topic) => `Got it — <b>${topic}</b>. What would you like to know?\n(You can still ask about anything else too — I'll figure it out.)`,
  hi: (topic) => `ठीक है — <b>${topic}</b>. आप क्या जानना चाहेंगे?\n(आप कुछ और भी पूछ सकते हैं — मैं समझ जाऊँगा.)`,
  mr: (topic) => `ठीक आहे — <b>${topic}</b>. तुम्हाला काय जाणून घ्यायचे आहे?\n(तुम्ही इतर काहीही विचारू शकता — मी समजून घेईन.)`,
};

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ChatbotComponent implements OnInit, AfterViewChecked {

  @ViewChild('chatBody') private chatBody: ElementRef;

  messages: Message[] = [];
  input = '';
  isLoading = false;
  userName = '';

  siteId: number = null;
  workOrderId: number = null;
  branchId: number = null;
  conversationHistory: { role: string, content: string }[] = [];

  // ── Reply language (persisted per-browser, same pattern as branchId) ───

  // ── Reply language (persisted per-browser, same pattern as branchId) ───
  language: string = 'en';
  languageOptions: LanguageOption[] = LANGUAGE_OPTIONS;
  showLanguageMenu = false;   // gear icon toggles this

  // ── Topic picker (static, instant — no network round-trip) ───────────────
  categories: TopicCategory[] = TOPIC_TREE;
  activeCategory: TopicCategory | null = null;
  selectedModule: string | null = null;
  showModuleSelector = true;

  constructor(private chatbot: ChatbotService) {}

  ngOnInit() {
    try {
      const raw = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
      const user = raw ? JSON.parse(raw) : null;
      this.userName = user?.firstName || user?.username || 'there';
      // FIX: the old `parseInt(x || '0') || null` collapsed a real branch id
      // of 0 down to null, silently disabling the mandatory branch filter.
      const branchRaw = localStorage.getItem('branch');
      const parsedBranch = branchRaw !== null && branchRaw !== undefined && branchRaw !== ''
        ? parseInt(branchRaw, 10)
        : NaN;
      this.branchId = Number.isNaN(parsedBranch) ? null : parsedBranch;
      console.log('branchId resolved:', this.branchId);

      // Restore previously chosen reply language, default English.
      const savedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      const knownCodes = this.languageOptions.map(l => l.code);
      this.language = savedLang && knownCodes.includes(savedLang) ? savedLang : 'en';
    } catch {}

    // Role-based topic picker filtering — previously every logged-in user
    // saw every category/topic in the picker regardless of role. This
    // fetches the backend's role-filtered module list (GET /modules is now
    // role-aware — see chatbot.service.ts / chat_router.py) and hides any
    // curated topic that doesn't match an allowed module. Full-access
    // roles (ROLE_ADMIN, CMD, aliases) get the backend's unfiltered list
    // back, so nothing changes for them.
    // NOTE: this is a UX convenience, not the security boundary — the
    // backend still blocks disallowed data at query time regardless of
    // what the picker shows (see EntityNotAuthorizedError / chat_router.py).
    this.chatbot.getModules().subscribe({
      next: (res) => this.applyRoleFilterToCategories(res?.modules || []),
      error: (err) => console.error('getModules() failed — topic picker left unfiltered:', err)
      // fail-open now applies ONLY to this real error case. A successful
      // response with an empty array is handled correctly above.
    });

    const greet = GREETING_TEXT[this.language] || GREETING_TEXT['en'];
    this.messages = [{
      role: 'bot',
      text: greet(this.userName),
      time: this.now()
    }];
  }

  /** Hides picker categories/items the logged-in role isn't allowed to
   *  access, using the real DB module names returned by the backend
   *  (already filtered server-side via get_allowed_modules_for_role). A
   *  curated topic label is kept if it loosely matches (substring, either
   *  direction, case-insensitive) any allowed module name — these labels
   *  are UI-only groupings and don't always equal the literal module name.
   *  Categories left with zero visible items are dropped entirely. */
  private applyRoleFilterToCategories(modules: { id: any, name: string }[]) {
    // Only skip filtering when the response itself is missing (undefined/
    // null — malformed or unexpected payload). An empty ARRAY is a
    // legitimate, successful answer meaning "this role has zero allowed
    // modules" and MUST still be applied — treating [] the same as "call
    // failed" is exactly what let a restricted role see every category.
    if (!modules) return;
    const allowedNames = modules.map(m => (m.name || '').toLowerCase()).filter(Boolean);
    const isAllowed = (item: string) => {
      const lower = item.toLowerCase();
      return allowedNames.some(name => lower.includes(name) || name.includes(lower));
    };
    this.categories = TOPIC_TREE
      .map(cat => ({ ...cat, items: cat.items.filter(isAllowed) }))
      .filter(cat => cat.items.length > 0);

    // A role left with zero picker-relevant categories shouldn't see an
    // empty grid — collapse straight to the input so they can just type.
    if (this.categories.length === 0) {
      this.showModuleSelector = false;
    }
  }

  ngAfterViewChecked() { this.scrollToBottom(); }

  private scrollToBottom() {
    try {
      const el = this.chatBody?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch {}
  }

  private now(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  // ── Language switcher ─────────────────────────────────────────────────


  toggleLanguageMenu() {
    this.showLanguageMenu = !this.showLanguageMenu;
  }

  /** Current language's UI string pack — used all over the template. */
  get t(): Record<string, string> {
    return UI_TEXT[this.language] || UI_TEXT['en'];
  }

  /** Closes the language dropdown on any click outside it or the gear
   *  button (gear lives in app.component's header, tagged .lang-gear-btn). */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showLanguageMenu) return;
    const target = event.target as HTMLElement;
    if (target.closest('.lang-menu-panel') || target.closest('.lang-gear-btn')) return;
    this.showLanguageMenu = false;
  }

  /** Changes the language future replies come back in, confirms the
   *  switch with a short message IN THE NEW LANGUAGE so the conversation
   *  visibly continues in that language from here on. Doesn't retranslate
   *  messages already on screen, and doesn't affect what the user can type
   *  in (they could already ask in Hindi/Marathi before this existed — the
   *  underlying model understands input in any language regardless). */
  setLanguage(code: string) {
    this.showLanguageMenu = false;
    if (this.language === code) return;
    this.language = code;
    try { localStorage.setItem(LANGUAGE_STORAGE_KEY, code); } catch {}

    this.messages.push({
      role: 'bot',
      text: LANGUAGE_SWITCH_CONFIRM[code] || LANGUAGE_SWITCH_CONFIRM['en'],
      time: this.now()
    });
  }

  // ── Topic picker navigation ───────────────────────────────────────────────

  toggleModuleBrowser() {
    this.showModuleSelector = !this.showModuleSelector;
    if (!this.showModuleSelector) {
      this.activeCategory = null;
    }
  }

  /** Level 1 → Level 2: drill into a category's subtopics. */
  selectCategory(cat: TopicCategory) {
    this.activeCategory = cat;
  }

  /** Level 2 → Level 1: back out to the top-level categories. */
  backToCategories() {
    this.activeCategory = null;
  }

  /** Leaf topic picked — finalize, close the picker, confirm in chat. */
  selectModule(topicName: string) {
    this.selectedModule = topicName;
    this.activeCategory = null;
    this.showModuleSelector = false;
    this.messages.push({
      role: 'bot',
      text: (SELECT_MODULE_TEXT[this.language] || SELECT_MODULE_TEXT['en'])(topicName),
      time: this.now()
    });
  }

  /** User skips picking a topic and just wants to type freely. */
  skipModuleSelection() {
    this.showModuleSelector = false;
    this.selectedModule = null;
    this.activeCategory = null;
  }

  /** Lets user change topic mid-conversation without losing chat history. */
  resetModule() {
    this.selectedModule = null;
    this.activeCategory = null;
    this.showModuleSelector = true;
  }

  // ── Chat ───────────────────────────────────────────────────────────────────

  quickAsk(q: string) { this.input = q; this.send(); }

  send() {
    if (!this.input.trim() || this.isLoading) return;
    const question = this.input.trim();
    this.showModuleSelector = false;   // collapse the picker once chat starts — breadcrumb bar takes over
    this.activeCategory = null;
    this.messages.push({ role: 'user', text: question, time: this.now() });
    this.input = '';
    this.isLoading = true;
    this.messages.push({ role: 'bot', text: '...', loading: true });

    this.chatbot.ask(
      question,
      this.siteId,
      this.workOrderId,
      this.conversationHistory,
      this.selectedModule,
      this.branchId,
      this.language
    ).subscribe({
      next: (res) => {
        this.messages = this.messages.filter(m => !m.loading);
        const botAnswer = res?.answer || 'No answer returned.';
        this.messages.push({
          role: 'bot',
          text: botAnswer,
          time: this.now(),
          rows: res?.rows || null,
          suggestions: res?.suggestions || [],
          isAnswer: true,          // real backend answer — eligible for copy/like/dislike
          logId: res?.log_id,
          feedback: null,
          copied: false
        });

        // Update conversation history for context
        this.conversationHistory.push({ role: 'user',      content: question });
        this.conversationHistory.push({ role: 'assistant', content: botAnswer });
        // Keep only last 10 turns to avoid bloat
        if (this.conversationHistory.length > 10) {
          this.conversationHistory = this.conversationHistory.slice(-10);
        }

        this.isLoading = false;
      },
      error: (err) => {
        this.messages = this.messages.filter(m => !m.loading);
        this.messages.push({
          role: 'bot',
          text: 'Error connecting to chatbot. Make sure the Python server is running.',
          time: this.now()
        });
        this.isLoading = false;
        console.error('Chatbot error:', err);
      }
    });
  }

  onKey(e: KeyboardEvent) { if (e.key === 'Enter') this.send(); }

  // ── Copy + feedback (lightweight — no polling, no blocking calls) ───────────

  copyAnswer(m: Message) {
    const plain = (m.text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    navigator.clipboard.writeText(plain).then(() => {
      m.copied = true;
      setTimeout(() => (m.copied = false), 1500);
    }).catch(() => {});
  }

  /** Click again on the same thumb to un-toggle (no backend call on un-toggle). */
  sendFeedback(m: Message, type: 'like' | 'dislike') {
    const next = m.feedback === type ? null : type;
    m.feedback = next;
    if (!next || !m.logId) return;
    this.chatbot.feedback(m.logId, next, undefined, m.text)
      .subscribe({ error: () => {} });   // best-effort, never disrupts the UI
  }

  // ── Export helpers (unchanged) ──────────────────────────────────────────────

  downloadCsv(m: Message) {
    if (!m.rows?.length) return;
    const headers = Object.keys(m.rows[0]);
    const csvRows = [
      headers.join(','),
      ...m.rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `erp-export-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  downloadAnswer(m: Message) {
    // If raw rows available, use structured CSV
    if (m.rows?.length) {
      this.downloadCsv(m);
      return;
    }

    // Otherwise parse the HTML table from the answer text into CSV
    const parser  = new DOMParser();
    const doc     = parser.parseFromString(m.text || '', 'text/html');
    const table   = doc.querySelector('table');

    let csvContent = '';

    if (table) {
      const rows = Array.from(table.querySelectorAll('tr'));
      csvContent = rows.map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        return cells.map(c => `"${c.textContent?.trim().replace(/"/g, '""') ?? ''}"`).join(',');
      }).join('\n');
    } else {
      // No table — just dump plain text of the answer
      const plain = doc.body.textContent?.trim() || m.text || '';
      csvContent  = `"Question","Answer"\n"${plain.replace(/"/g, '""')}"`;
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `erp-export-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}